/**
 * Permissions Catalog — single source of truth.
 *
 * Permission keys follow the format: `<resource>[.<sub_resource>]:<action>`
 *
 * The DATABASE stores only:
 *   - Roles (name + permissions array)
 *   - User → role assignments
 *
 * The CATALOG (this file) is shipped with the application bundle.
 * Adding a new permission = edit this file. No DB migration needed.
 */

// ─────────────────────────────────────────────────────────────────────────
// ACTIONS — fixed vocabulary, do not invent new verbs without discussion
// ─────────────────────────────────────────────────────────────────────────

export const ACTIONS = [
    'view',     // read access
    'create',   // make a new instance
    'edit',     // modify existing
    'delete',   // remove (soft-delete in this codebase)
    'issue',    // generate a document (vouchers, invoices, etc.)
    'cancel',   // cancel an issued document (or a travel file: Open / In Process → Cancelled, and undo it)
    'export',   // download / print / send to external system
    'email',    // send a document by email (typically a PDF attachment)
    'approve',  // approve a document (e.g. PRQ approval before RMT)
    'charge'    // move money from a customer's saved card (customer_card:charge)
] as const;

export type Action = (typeof ACTIONS)[number];

// ─────────────────────────────────────────────────────────────────────────
// RESOURCES — what the application protects
// ─────────────────────────────────────────────────────────────────────────

export const RESOURCES = [
    // ── Travel Files ────────────────────────────────────────────────────
    'travel_file',                  // the file itself
    'travel_file.passenger',        // passengers within a file
    'travel_file.rooming',          // room assignments (group travel files)
    'travel_file.note',             // notes / remarks
    'travel_file.history',          // activity log
    'travel_file.accounting',       // the travel file's Accounting tab (documents + totals)

    // ── Reservations: tour inventory ────────────────────────────────────
    'tour',                         // Book Tour + Tour Booking (and its passenger / account / hotel tabs)

    // ── Services (one per type) ─────────────────────────────────────────
    'service.flight',
    'service.hotel',
    'service.tour',
    'service.guide',
    'service.package',
    'service.general',
    'service.visa',

    // ── Documents ───────────────────────────────────────────────────────
    'voucher',
    'invoice',
    'tax_invoice',
    'agent_invoice',
    'receipt',
    'prq',                          // payment requisition
    'remittance',
    'refund',
    'credit_debit_note',
    'customer_card',                // a passenger's saved card (Pelecard token) — view / charge / delete

    // ── Financial workflows ─────────────────────────────────────────────
    'cashier',
    'cancelled_receipts',           // finance review of cancelled card receipts
    'pelecard_transactions',        // Pelecard's own transaction list, matched to Taytan's charges
    'supplier_reconciliation',
    'bsp_reconciliation',
    'agent_reconciliation',
    'payment_book',
    'transfer_book',
    'general_ledger',
    'prq_approval',                 // waiting for approval page

    // ── Reports ─────────────────────────────────────────────────────────
    'report.sales',
    'report.tour_details',
    'report.tour_registration',
    'report.exchange_rates',
    'report.rct_trc',
    'report.documents',
    'report.prq_rmt',
    'report.travel_file_inventory',
    'report.supplier_turnover',
    'report.supplier_turnover_summary',
    'report.reservations',

    // ── Dashboard widgets ───────────────────────────────────────────────
    'dashboard.stats',
    'dashboard.quick_actions',
    'dashboard.upcoming_departures',
    'dashboard.recent_files',
    'dashboard.travel_files_chart',
    'dashboard.passengers_chart',
    'dashboard.revenue_chart',
    'dashboard.open_prqs',
    'dashboard.latest_rates',
    'dashboard.recent_vouchers',

    // ── Setup: Finance ──────────────────────────────────────────────────
    'setup.bank_account',
    'setup.credit_card',
    'setup.general_account',
    'setup.income_account',
    'setup.discount_account',
    'setup.agent_commission_account',
    'setup.currency',
    'setup.credit_company',
    'setup.credit_payment_option',
    'setup.payments_terminal',
    'setup.income_type',
    'setup.event',
    'setup.document_type_numbering',
    'setup.document_approval',

    // ── Setup: Business Rules ───────────────────────────────────────────
    'setup.general_info',
    'setup.supplier_account',
    'setup.market',
    'setup.travel_agent_type',
    'setup.travel_agency_account',

    // ── Setup: Product Management ───────────────────────────────────────
    'setup.service_abbreviation',
    'setup.service_attribute',
    'setup.service_type',
    'setup.category',
    'setup.price_level',
    'setup.price_component',
    'setup.hotel_facility',
    'setup.hotel',
    'setup.guide_language',
    'setup.passenger_attribute',
    'setup.component_level',
    'setup.component_type',
    'setup.supplier_interface',
    'setup.class',

    // ── Setup: General lookups ──────────────────────────────────────────
    'setup.payment_option',
    'setup.crm_action',
    'setup.crm_type',
    'setup.email_engagement',
    'setup.email_template',         // reusable message templates; :edit covers add/edit/delete. Authoring only — anyone who may email a document may pick one
    'setup.country',
    'setup.destination',
    'setup.location',
    'setup.travel_file_attribute',
    'setup.group_travel_file',
    'setup.queue',                  // work queues assigned to user types
    'setup.passenger',              // global CRM passenger directory (GDPR-sensitive)

    // ── Setup: Operation & Products ─────────────────────────────────────
    'setup.report_reservation_supplier',
    'setup.update_reservation_supplier',
    'setup.contract',
    'setup.escorted_tour',           // formerly "self-operation tours"
    'setup.tour_registration_rule',  // customer-facing tour registration rules (deposit / payment window)

    // ── Financial: Documents ────────────────────────────────────────────
    'issue_documents',               // Issue Documents page
    'find_documents',                // Find Documents page

    // ── Financial: Rates of Exchange ────────────────────────────────────
    'exchange_rate',                 // view + edit rates
    'exchange_rate.limit',           // change the percent change limit
    'exchange_rate.auto_override',   // edit rates even while auto-update is on

    // ── Admin / System ──────────────────────────────────────────────────
    'admin.user',
    'admin.role',
    'system.database',
    'system.grid_settings',
    'system.upload_users',
    'system.welcome_email',          // day-one welcome mail, addressed from a curated CSV
    'system.app_settings',
    'system.appearance',             // design tokens: shape, density, navigation
    'system.amadeus',
    'system.email_settings',
    'system.signature',              // personal digital signature for document approvals
    'system.tickets',                // bug / improvement ticket triage board
    'system.jira'                    // Jira integration settings for ticket push
] as const;

export type Resource = (typeof RESOURCES)[number];

// ─────────────────────────────────────────────────────────────────────────
// PERMISSIONS — every valid resource:action pair the app uses
// ─────────────────────────────────────────────────────────────────────────
//
// Not every resource has every action. We declare only the pairs that
// the application actually checks. If a permission isn't declared here,
// it doesn't exist — TypeScript will reject any reference to it.
//

type PermissionMeta = {
    label: string;
    description: string;
    category: string;
};

export const PERMISSIONS = {
    // ── Reservations ────────────────────────────────────────────────────
    // These gate the Reservation › Tour pages, which until now had no
    // permission of any kind: any signed-in user could reach them.
    'tour:view': {
        label: 'Tour Booking',
        description: 'Open Tour Booking and its passenger list, account summary and hotel list',
        category: 'Reservations'
    },
    'tour:book': {
        label: 'Book Tour',
        description: 'Open Book Tour and start a booking from an existing tour',
        category: 'Reservations'
    },

    // ── Travel Files ────────────────────────────────────────────────────
    'travel_file:view': {
        label: 'View Travel Files',
        description: 'See travel files in the list and open them',
        category: 'Travel Files'
    },
    'travel_file:create': {
        label: 'Create Travel File',
        description: 'Create new individual or group travel files',
        category: 'Travel Files'
    },
    'travel_file:edit': {
        label: 'Edit Travel File',
        description: 'Modify travel file fields and metadata',
        category: 'Travel Files'
    },
    'travel_file:delete': {
        label: 'Delete Travel File',
        description: 'Soft-delete a travel file',
        category: 'Travel Files'
    },
    'travel_file:close': {
        label: 'Close Travel File',
        description: 'Close a travel file (replaces previous CLOSE_TRAVEL_FILE)',
        category: 'Travel Files'
    },
    'travel_file:reopen': {
        label: 'Reopen Travel File',
        description: 'Reopen a closed travel file',
        category: 'Travel Files'
    },
    'travel_file:balance': {
        label: 'Balance Travel File',
        description: 'Run the Balance Travel File operation',
        category: 'Travel Files'
    },
    'travel_file:send_to_crm': {
        label: 'Send Travel File to CRM',
        description:
            "Send a travel file to the CRM again — e.g. after completing passenger details the CRM refused it for",
        category: 'Travel Files'
    },
    'travel_file:adopt_from_tb': {
        label: 'Export Travel File to Taytan',
        description:
            'Move a TravelBooster travel file onto a Taytan tour, with its passengers, documents and history',
        category: 'Travel Files'
    },
    'travel_file:confirm_without_payment': {
        label: 'Confirm Travel File Without Payment',
        description:
            'Open an In Process travel file that has no payment — a guide, a passenger travelling free — optionally at no charge, with a note saying why',
        category: 'Travel Files'
    },
    'travel_file:moved_accounting_override': {
        label: 'Override Moved-File Accounting Lock',
        description:
            'Issue, cancel or amend a document on a travel file moved from TravelBooster, whose accounting is in TB — with a reason written to the travel-file log',
        category: 'Travel Files'
    },
    'travel_file:cancel': {
        label: 'Cancel Travel File',
        description:
            'Cancel an individual travel file (Open / In Process → Cancelled), and undo that cancellation',
        category: 'Travel Files'
    },

    'travel_file.passenger:view': {
        label: 'View Passengers',
        description: 'See passengers within a travel file',
        category: 'Travel Files'
    },
    'travel_file.passenger:create': {
        label: 'Add Passenger',
        description: 'Add new passengers to a travel file',
        category: 'Travel Files'
    },
    'travel_file.passenger:edit': {
        label: 'Edit Passenger',
        description: 'Modify passenger details',
        category: 'Travel Files'
    },
    'travel_file.passenger:delete': {
        label: 'Remove Passenger',
        description: 'Remove a passenger from the travel file',
        category: 'Travel Files'
    },
    // "Mail to Client" on a passenger row. NOT a document email: the nine
    // `<document>:email` keys each gate sending a specific PDF, and this sends
    // free-form text the sender types, under the agency's branding, to whatever
    // address is on the passenger. It had no key at all — anyone who could open a
    // travel file could write to a customer as the agency. Reuses the existing
    // `email` verb rather than inventing one; it renders as an extra checkbox on
    // the Passengers row under Travel Files.
    'travel_file.passenger:email': {
        label: 'Email Client from Passenger',
        description:
            'Send a free-form email to a passenger from the passenger row ("Mail to Client"). ' +
            'Separate from the per-document email permissions, which cover sending a PDF.',
        category: 'Travel Files'
    },

    'travel_file.rooming:view': {
        label: 'View Rooming',
        description: 'See room assignments for passengers in group travel files',
        category: 'Travel Files'
    },
    'travel_file.rooming:edit': {
        label: 'Edit Rooming',
        description: 'Assign passengers to rooms in group travel files',
        category: 'Travel Files'
    },

    'travel_file.note:view': {
        label: 'View Notes',
        description: 'View notes and remarks on a travel file',
        category: 'Travel Files'
    },
    'travel_file.note:create': {
        label: 'Add Note',
        description: 'Add new notes/remarks to a travel file',
        category: 'Travel Files'
    },
    'travel_file.note:edit': {
        label: 'Edit Note',
        description: 'Modify existing notes/remarks',
        category: 'Travel Files'
    },
    // A user may always delete a note they wrote; this key is for deleting
    // everyone else's. Notes imported from TB stay read-only regardless.
    'travel_file.note:delete': {
        label: "Delete Others' Notes",
        description: "Delete travel file notes written by other users (anyone can delete their own)",
        category: 'Travel Files'
    },

    'travel_file.accounting:view': {
        label: 'View Travel File Accounting',
        description: "Open the travel file's Accounting tab — its documents and totals",
        category: 'Travel Files'
    },
    'travel_file.history:view': {
        label: 'View History',
        description: 'View travel file activity log',
        category: 'Travel Files'
    },

    // ── Services ────────────────────────────────────────────────────────
    // Same shape for every service type
    ...buildServicePerms('service.flight', 'Flight'),
    ...buildServicePerms('service.hotel', 'Hotel'),
    ...buildServicePerms('service.tour', 'Tour'),
    ...buildServicePerms('service.guide', 'Guide'),
    ...buildServicePerms('service.package', 'Package'),
    ...buildServicePerms('service.general', 'General'),
    ...buildServicePerms('service.visa', 'Visa'),

    // ── Documents ───────────────────────────────────────────────────────
    // Checked by the dashboard's "recent vouchers" quick action — the only
    // per-document read right anything in the app actually reads.
    'voucher:view': makeMeta('View Voucher', '', 'Travel File Documents'),
    ...buildDocumentPerms('voucher', 'Voucher'),
    ...buildDocumentPerms('invoice', 'Invoice'),
    ...buildDocumentPerms('tax_invoice', 'Tax Invoice'),
    ...buildDocumentPerms('agent_invoice', 'Agent Invoice'),
    ...buildDocumentPerms('receipt', 'Receipt'),
    ...buildDocumentPerms('prq', 'PRQ'),
    ...buildDocumentPerms('remittance', 'Remittance'),
    ...buildDocumentPerms('refund', 'Refund'),
    ...buildDocumentPerms('credit_debit_note', 'Credit/Debit Note'),

    // Saved customer cards (OpenSpec add-saved-customer-credit-cards). Granted by
    // grant_customer_card_permissions.sql to every role holding receipt:issue.
    // SAVING a card needs no key of its own: it rides on receipt:issue, and
    // happens automatically after a payment-page receipt for a passenger payer.
    'customer_card:view': makeMeta(
        'View Saved Customer Cards',
        "See a passenger's saved cards (brand, last 4 digits, expiry) on the passenger and receipt forms",
        'Travel File Documents'
    ),
    'customer_card:charge': makeMeta(
        'Charge a Saved Customer Card',
        'Pay a receipt with a saved card, without opening the payment page',
        'Travel File Documents'
    ),
    'customer_card:delete': makeMeta(
        'Delete Saved Customer Cards',
        "Remove a passenger's saved card; its token is erased and it can no longer be charged",
        'Travel File Documents'
    ),

    // ── Financial workflows ─────────────────────────────────────────────
    'cashier:view': makeMeta('Access Cashier', 'Open the Cashier page', 'Financial'),
    'cashier:edit': makeMeta('Use Cashier', 'Perform cashier operations', 'Financial'),
    // Granted by grant_cancelled_receipts_permissions.sql to every role holding cashier:view.
    'cancelled_receipts:view': makeMeta(
        'Access Cancelled Receipts',
        'See cancelled card receipts with their card details and Pelecard status',
        'Financial'
    ),
    'cancelled_receipts:edit': makeMeta(
        'Note Cancelled Receipts',
        "Write finance's note (the decision taken) on a cancelled card receipt",
        'Financial'
    ),
    // Granted by grant_pelecard_transactions_permissions.sql to every role holding cashier:view.
    'pelecard_transactions:view': makeMeta(
        'Access Pelecard Transactions',
        "See Pelecard's transaction list per terminal and date range, matched to Taytan's card charges",
        'Financial'
    ),

    'supplier_reconciliation:view': makeMeta('Access Supplier Reconciliation', '', 'Financial'),
    'supplier_reconciliation:edit': makeMeta('Use Supplier Reconciliation', '', 'Financial'),

    'bsp_reconciliation:view': makeMeta('Access BSP Reconciliation', '', 'Financial'),
    'bsp_reconciliation:edit': makeMeta('Use BSP Reconciliation', '', 'Financial'),

    'agent_reconciliation:view': makeMeta('Access Travel Agent Reconciliation', '', 'Financial'),
    'agent_reconciliation:edit': makeMeta('Use Travel Agent Reconciliation', '', 'Financial'),

    'payment_book:view': makeMeta('View Payment Book', '', 'Financial'),
    'payment_book:edit': makeMeta('Use Payment Book', '', 'Financial'),

    'transfer_book:view': makeMeta('View Transfer Book', '', 'Financial'),
    'transfer_book:edit': makeMeta('Use Transfer Book', '', 'Financial'),

    'general_ledger:view': makeMeta('View General Ledger Export', '', 'Financial'),
    'general_ledger:export': makeMeta('Export to General Ledger', '', 'Financial'),

    'prq_approval:view': makeMeta('Access Document Approval', 'Open the Document Approval page', 'Financial'),
    'prq:approve': makeMeta('Approve PRQ', 'Approve Payment Requisitions', 'Financial'),

    // ── Financial: Documents (split from the legacy "Issue & Find Documents") ──
    'issue_documents:view': makeMeta('Access Issue Documents', 'Issue receipts, invoices, remittances and credit/debit notes', 'Financial'),
    'find_documents:view': makeMeta('Access Find Documents', 'Search and view existing issued documents', 'Financial'),

    // ── Reports ─────────────────────────────────────────────────────────
    'report.sales:view': makeMeta('View Sales Report', '', 'Reports'),
    'report.sales:export': makeMeta('Export Sales Report', '', 'Reports'),

    'report.tour_details:view': makeMeta('View Tour Details Report', '', 'Reports'),
    'report.tour_details:export': makeMeta('Export Tour Details Report', '', 'Reports'),

    'report.tour_registration:view': makeMeta('View Tour Registration Report', '', 'Reports'),
    'report.tour_registration:export': makeMeta('Export Tour Registration Report', '', 'Reports'),

    'report.exchange_rates:view': makeMeta('View Exchange Rates Report', '', 'Reports'),
    'report.exchange_rates:export': makeMeta('Export Exchange Rates Report', '', 'Reports'),

    'report.rct_trc:view': makeMeta('View RCT/TRC Report', '', 'Reports'),
    'report.rct_trc:export': makeMeta('Export RCT/TRC Report', '', 'Reports'),

    'report.documents:view': makeMeta('View Documents Report', '', 'Reports'),
    'report.documents:export': makeMeta('Export Documents Report', '', 'Reports'),

    'report.prq_rmt:view': makeMeta('View PRQ/RMT Report', '', 'Reports'),
    'report.prq_rmt:export': makeMeta('Export PRQ/RMT Report', '', 'Reports'),

    'report.travel_file_inventory:view': makeMeta('View Travel File Inventory Report', '', 'Reports'),
    'report.travel_file_inventory:export': makeMeta('Export Travel File Inventory Report', '', 'Reports'),

    'report.supplier_turnover:view': makeMeta('View Supplier Turnover Report', '', 'Reports'),
    'report.supplier_turnover:export': makeMeta('Export Supplier Turnover Report', '', 'Reports'),

    'report.supplier_turnover_summary:view': makeMeta('View Supplier Turnover Summary Report', '', 'Reports'),
    'report.supplier_turnover_summary:export': makeMeta('Export Supplier Turnover Summary Report', '', 'Reports'),

    'report.reservations:view': makeMeta('View Reservations Report', '', 'Reports'),
    'report.reservations:export': makeMeta('Export Reservations Report', '', 'Reports'),

    'report.cancelled_passengers:view': makeMeta('View Cancelled Passengers Report', '', 'Reports'),
    'report.cancelled_passengers:export': makeMeta('Export Cancelled Passengers Report', '', 'Reports'),

    'report.left_to_pay:view': makeMeta('View Left to Pay Report', '', 'Reports'),
    'report.left_to_pay:export': makeMeta('Export Left to Pay Report', '', 'Reports'),

    // ── Dashboard widgets ───────────────────────────────────────────────
    'dashboard.stats:view':                makeMeta('Stat Cards',          'Top stat cards (totals, counts)', 'Dashboard'),
    'dashboard.quick_actions:view':        makeMeta('Quick Actions',       '', 'Dashboard'),
    'dashboard.upcoming_departures:view':  makeMeta('Upcoming Departures', '', 'Dashboard'),
    'dashboard.recent_files:view':         makeMeta('Recent Travel Files', '', 'Dashboard'),
    'dashboard.travel_files_chart:view':   makeMeta('Travel Files Chart',  '', 'Dashboard'),
    'dashboard.passengers_chart:view':     makeMeta('Passengers Chart',    '', 'Dashboard'),
    'dashboard.revenue_chart:view':        makeMeta('Revenue Chart',       'Sensitive — shows revenue figures', 'Dashboard'),
    'dashboard.open_prqs:view':            makeMeta('Open PRQs',           'Sensitive — shows outstanding payment requisitions', 'Dashboard'),
    'dashboard.latest_rates:view':         makeMeta('Latest Rates',        'Sensitive — shows latest exchange rates', 'Dashboard'),
    'dashboard.recent_vouchers:view':      makeMeta('Recent Vouchers',     'Sensitive — shows recent voucher activity', 'Dashboard'),

    // ── Setup: Finance ──────────────────────────────────────────────────
    ...buildSetupPerms('setup.bank_account',           'Bank Accounts',           'Setup — Finance'),
    ...buildSetupPerms('setup.credit_card',            'Credit Cards',            'Setup — Finance'),
    ...buildSetupPerms('setup.general_account',        'General Accounts',        'Setup — Finance'),
    ...buildSetupPerms('setup.income_account',         'Income Accounts',         'Setup — Finance'),
    ...buildSetupPerms('setup.discount_account',       'Discount Accounts',       'Setup — Finance'),
    ...buildSetupPerms('setup.agent_commission_account','Agent Commission Accts', 'Setup — Finance'),
    ...buildSetupPerms('setup.currency',               'Currencies',              'Setup — Finance'),
    ...buildSetupPerms('setup.credit_company',         'Credit Companies',        'Setup — Finance'),
    ...buildSetupPerms('setup.credit_payment_option',  'Credit Payment Options',  'Setup — Finance'),
    ...buildSetupPerms('setup.payments_terminal',      'Payments Terminals',      'Setup — Finance'),
    ...buildSetupPerms('setup.income_type',            'Income Types',            'Setup — Finance'),
    ...buildSetupPerms('setup.event',                  'Events',                  'Setup — Finance'),
    ...buildSetupPerms('setup.document_type_numbering','Document Type Numbering', 'Setup — Finance'),
    ...buildSetupPerms('setup.document_approval',      'Finance General Settings', 'Setup — Finance'),

    // ── Setup: Business Rules ───────────────────────────────────────────
    ...buildSetupPerms('setup.general_info',           'General Info',            'Setup — Business Rules'),
    ...buildSetupPerms('setup.supplier_account',       'Supplier Accounts',       'Setup — Business Rules'),
    ...buildSetupPerms('setup.market',                 'Markets',                 'Setup — Business Rules'),
    ...buildSetupPerms('setup.travel_agent_type',      'Travel Agent Types',      'Setup — Business Rules'),
    ...buildSetupPerms('setup.travel_agency_account',  'Travel Agency Accounts',  'Setup — Business Rules'),

    // ── Setup: Product Management ───────────────────────────────────────
    ...buildSetupPerms('setup.service_abbreviation',   'Service Abbreviations',   'Setup — Product Management'),
    ...buildSetupPerms('setup.service_attribute',      'Service Attributes',      'Setup — Product Management'),
    ...buildSetupPerms('setup.service_type',           'Service Types',           'Setup — Product Management'),
    ...buildSetupPerms('setup.category',               'Categories',              'Setup — Product Management'),
    ...buildSetupPerms('setup.price_level',            'Price Levels',            'Setup — Product Management'),
    ...buildSetupPerms('setup.price_component',        'Price Components',        'Setup — Product Management'),
    ...buildSetupPerms('setup.hotel_facility',         'Hotel Facilities',        'Setup — Product Management'),
    ...buildSetupPerms('setup.hotel',                  'Hotels',                  'Setup — Product Management'),
    ...buildSetupPerms('setup.guide_language',         'Guide Languages',         'Setup — Product Management'),
    ...buildSetupPerms('setup.passenger_attribute',    'Passenger Attributes',    'Setup — Product Management'),
    ...buildSetupPerms('setup.component_level',        'Component Levels',        'Setup — Product Management'),
    ...buildSetupPerms('setup.component_type',         'Component Types',         'Setup — Product Management'),
    ...buildSetupPerms('setup.supplier_interface',     'Supplier Interface',      'Setup — Product Management'),
    ...buildSetupPerms('setup.class',                  'Classes',                 'Setup — Product Management'),

    // ── Setup: General lookups ──────────────────────────────────────────
    ...buildSetupPerms('setup.payment_option',         'Payment Options',         'Setup — General'),
    ...buildSetupPerms('setup.crm_action',             'CRM Actions',             'Setup — General'),
    ...buildSetupPerms('setup.crm_type',               'CRM Types',               'Setup — General'),
    ...buildSetupPerms('setup.email_engagement',       'Email Engagements',       'Setup — General'),
    ...buildSetupPerms('setup.email_template',         'Email Templates',         'Setup — General'),
    ...buildSetupPerms('setup.country',                'Countries',               'Setup — General'),
    ...buildSetupPerms('setup.destination',            'Destinations',            'Setup — General'),
    ...buildSetupPerms('setup.location',               'Locations',               'Setup — General'),
    ...buildSetupPerms('setup.travel_file_attribute',  'Travel File Attributes',  'Setup — General'),
    // Gates the "Group Travel File" button on a CLIENT travel file — the one that
    // switches to the group the booking belongs to. Filed under Setup — General
    // with an empty description, it read as a Setup screen nobody could find, and
    // no role preset grants it: 13 of 17 roles on PROD lacked it, the whole
    // service department among them. Named and described for what it actually
    // does, so it is findable by someone looking for the button.
    'setup.group_travel_file:view': makeMeta(
        'Open the Group Travel File',
        'Switch from a client travel file to the group file it belongs to (the "Group Travel File" button).',
        'Travel Files'
    ),
    ...buildSetupPerms('setup.queue',                  'Queues',                  'Setup — General'),
    ...buildSetupPerms('setup.passenger',              'Passengers (CRM)',        'Setup — General'),

    // ── Operations ──────────────────────────────────────────────────────
    // The three built pages of the Operations menu. They were filed under
    // 'Setup — Operation', which put them inside the Setup block of the role
    // editor — nowhere near the Operations section they belong to, so an
    // administrator looking for "Escorted Tours" could not find it.
    //
    // Only the display category moved. The keys still read `setup.*` because
    // renaming them would invalidate the expanded key lists stored on all 17
    // existing roles.
    'setup.report_reservation_supplier:view': makeMeta('View Report Reservation to Supplier', '', 'Operations'),
    'setup.update_reservation_supplier:view': makeMeta('View Update Reservation from Supplier', '', 'Operations'),
    ...buildSetupPerms('setup.escorted_tour',          'Escorted Tours',          'Operations'),

    // ── Setup: Products ─────────────────────────────────────────────────
    ...buildSetupPerms('setup.contract',               'Contracts',               'Setup — Product Management'),
    ...buildSetupPerms('setup.tour_registration_rule', 'Tour Registration Rules', 'Setup — General'),

    // ── Financial: Rates of Exchange ────────────────────────────────────
    'exchange_rate:view':       makeMeta('View Rates of Exchange',          '', 'Financial'),
    'exchange_rate:edit':       makeMeta('Edit Rates of Exchange',          '', 'Financial'),
    'exchange_rate.limit:edit': makeMeta('Change Rate of Exchange Limit',   '', 'Financial'),
    'exchange_rate.auto_override:edit': makeMeta('Edit Rates While Auto-Update Is On', 'Manually edit rates even when automatic Bank Leumi updates are enabled', 'Financial'),

    // ── Users & Roles ──────────────────────────────────────────────────
    'admin.user:view': makeMeta('View Users', '', 'Users & Roles'),
    'admin.user:edit': makeMeta('Manage Users', 'Create, edit, and delete users', 'Users & Roles'),

    'admin.role:view': makeMeta('View Roles', '', 'Users & Roles'),
    'admin.role:edit': makeMeta('Manage Roles & Permissions', 'Create, edit, and delete roles', 'Users & Roles'),

    // ── System ─────────────────────────────────────────────────────────

    'system.database:view': makeMeta('Database Access', '', 'System'),
    'system.grid_settings:edit': makeMeta('Grid Settings', '', 'System'),
    'system.upload_users:edit': makeMeta('Upload Users', '', 'System'),
    // Deliberately its own key rather than riding on Upload Users. Uploading a
    // CSV changes rows; this one puts mail in the inbox of everybody named in the
    // file, which is not undoable and is not the same right.
    'system.welcome_email:edit': makeMeta(
        'Send Welcome Email',
        'Send the day-one welcome email to a list of people curated in a CSV. Administrator only.',
        'System'
    ),
    'system.app_settings:edit': makeMeta('App Settings', 'Maintenance mode and system-wide settings', 'System'),
    'system.appearance:edit': makeMeta(
        'Appearance & Density',
        'Tune the design tokens the whole app renders from — shape, density, surfaces and navigation',
        'System'
    ),
    'system.amadeus:view': makeMeta('Amadeus Settings', '', 'Setup — General'),
    'system.email_settings:edit': makeMeta('Email Settings', '', 'Setup — General'),
    'system.signature:view': makeMeta('My Signature', 'Draw and save a personal digital signature for document approvals', 'Setup — General'),
    'system.tickets:view': makeMeta(
        'Ticket Board',
        'Triage bug reports and improvement requests submitted from within the app',
        'System'
    ),
    'system.tickets:edit': makeMeta(
        'Ticket Triage',
        'Change ticket status, severity and category, and post internal notes',
        'System'
    ),
    'system.jira:edit': makeMeta(
        'Jira Integration',
        'Configure the Jira connection and how each feedback type maps to a Jira project and issue type',
        'Setup — General'
    )
} as const satisfies Record<string, PermissionMeta>;

export type PermissionKey = keyof typeof PERMISSIONS;

// ─────────────────────────────────────────────────────────────────────────
// ROLE PRESETS — pre-built bundles for common job types
// ─────────────────────────────────────────────────────────────────────────

/**
 * Presets are grouped by tier when shown in the role editor dropdown:
 *   - 'ic'         — Individual Contributor (does the work, may be junior or senior)
 *   - 'department' — Department Head / VP (oversees one department)
 *   - 'company'    — Company-Wide (oversees multiple departments or the whole company)
 *   - 'special'    — Read-only auditor and other non-tiered roles
 */
type PresetCategory = 'ic' | 'department' | 'company' | 'special';

type Preset = {
    label: string;
    description: string;
    category: PresetCategory;
    /** Concrete keys OR wildcards (expanded at apply time, not stored on the role) */
    permissions: string[];
    /** Optional: extend another preset and only specify what's added */
    extends?: string;
    additional?: string[];
};

export const ROLE_PRESETS = {
    read_only_auditor: {
        label: 'Read-only Auditor',
        description: 'Can see everything, change nothing.',
        category: 'special',
        permissions: ['*:view']
    },

    sales: {
        label: 'Sales',
        description: 'Sales department: books from existing tours (via Tour Booking), creates travel files, manages passengers and services. Issues operational documents (vouchers) but not financial documents (invoices, receipts).',
        category: 'ic',
        // Snapshot of the live "Sales" role on PROD, 2026-09-23.
        permissions: [
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'invoice:cancel',
            'invoice:email',
            'invoice:issue',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'service.flight:view',
            'service.general:view',
            'service.guide:view',
            'service.hotel:view',
            'service.package:view',
            'service.tour:view',
            'service.visa:view',
            'setup.group_travel_file:view',
            'tax_invoice:cancel',
            'tax_invoice:email',
            'tax_invoice:issue',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:view',
            'travel_file.passenger:email',
            'travel_file.passenger:view',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:cancel',
            'travel_file:close',
            'travel_file:create',
            'travel_file:reopen',
            'travel_file:view',
            'voucher:view',
        ]
    },

    sales_senior: {
        label: 'Sales Senior',
        description: 'Senior sales desk: Sales plus document cancellation and the wider report set.',
        category: 'ic',
        // Snapshot of the live "Sales Senior" role on PROD, 2026-09-23.
        permissions: [
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'invoice:cancel',
            'invoice:email',
            'invoice:issue',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'service.flight:create',
            'service.flight:edit',
            'service.flight:view',
            'service.general:create',
            'service.general:edit',
            'service.general:view',
            'service.guide:create',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:create',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:create',
            'service.package:edit',
            'service.package:view',
            'service.tour:create',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:create',
            'service.visa:edit',
            'service.visa:view',
            'setup.group_travel_file:view',
            'tax_invoice:cancel',
            'tax_invoice:email',
            'tax_invoice:issue',
            'tour:book',
            'tour:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:delete',
            'travel_file.passenger:edit',
            'travel_file.passenger:email',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:cancel',
            'travel_file:close',
            'travel_file:create',
            'travel_file:edit',
            'travel_file:reopen',
            'travel_file:view',
            'voucher:view',
        ]
    },

    operator: {
        label: 'Operator',
        description: 'Operations agent: full handling of travel files, passengers, services, and financial documents (issue invoices, receipts, PRQs, remittances, refunds).',
        category: 'ic',
        // Snapshot of the live "Operator" role on PROD, 2026-09-23.
        permissions: [
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'service.flight:create',
            'service.flight:edit',
            'service.flight:view',
            'service.general:create',
            'service.general:edit',
            'service.general:view',
            'service.guide:create',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:create',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:create',
            'service.package:edit',
            'service.package:view',
            'service.tour:create',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:create',
            'service.visa:edit',
            'service.visa:view',
            'setup.country:edit',
            'setup.country:view',
            'setup.destination:edit',
            'setup.destination:view',
            'setup.escorted_tour:edit',
            'setup.escorted_tour:view',
            'setup.group_travel_file:view',
            'setup.hotel:edit',
            'setup.hotel:view',
            'setup.hotel_facility:edit',
            'setup.hotel_facility:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:delete',
            'travel_file.passenger:edit',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:create',
            'travel_file:edit',
            'travel_file:view',
            'voucher:cancel',
            'voucher:email',
            'voucher:issue',
            'voucher:view',
        ]
    },

    operator_senior: {
        label: 'Operator Senior',
        description: 'Operator + can cancel documents, close/reopen travel files, balance, delete services.',
        category: 'ic',
        // Snapshot of the live "Operator Senior" role on PROD, 2026-09-23.
        permissions: [
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'service.flight:cancel',
            'service.flight:create',
            'service.flight:delete',
            'service.flight:edit',
            'service.flight:view',
            'service.general:cancel',
            'service.general:create',
            'service.general:delete',
            'service.general:edit',
            'service.general:view',
            'service.guide:cancel',
            'service.guide:create',
            'service.guide:delete',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:cancel',
            'service.hotel:create',
            'service.hotel:delete',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:cancel',
            'service.package:create',
            'service.package:delete',
            'service.package:edit',
            'service.package:view',
            'service.tour:cancel',
            'service.tour:create',
            'service.tour:delete',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:cancel',
            'service.visa:create',
            'service.visa:delete',
            'service.visa:edit',
            'service.visa:view',
            'setup.escorted_tour:edit',
            'setup.escorted_tour:view',
            'setup.group_travel_file:view',
            'setup.hotel:edit',
            'setup.hotel:view',
            'setup.hotel_facility:edit',
            'setup.hotel_facility:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:delete',
            'travel_file.passenger:edit',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:cancel',
            'travel_file:close',
            'travel_file:create',
            'travel_file:edit',
            'travel_file:reopen',
            'travel_file:view',
            'voucher:cancel',
            'voucher:email',
            'voucher:issue',
            'voucher:view',
        ]
    },

    aviation: {
        label: 'Aviation Team',
        description: 'Aviation desk: full handling of travel files, services of every type and vouchers — create, edit, delete and cancel. Sees the accounting tab but issues no financial documents (invoices, receipts, PRQs, remittances).',
        category: 'ic',
        // Snapshot of the live "Aviation Team" role on PROD, 2026-09-23.
        permissions: [
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'report.tour_details:export',
            'report.tour_details:view',
            'service.flight:cancel',
            'service.flight:create',
            'service.flight:delete',
            'service.flight:edit',
            'service.flight:view',
            'service.general:cancel',
            'service.general:create',
            'service.general:delete',
            'service.general:edit',
            'service.general:view',
            'service.guide:cancel',
            'service.guide:create',
            'service.guide:delete',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:cancel',
            'service.hotel:create',
            'service.hotel:delete',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:cancel',
            'service.package:create',
            'service.package:delete',
            'service.package:edit',
            'service.package:view',
            'service.tour:cancel',
            'service.tour:create',
            'service.tour:delete',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:cancel',
            'service.visa:create',
            'service.visa:delete',
            'service.visa:edit',
            'service.visa:view',
            'setup.escorted_tour:edit',
            'setup.escorted_tour:view',
            'setup.group_travel_file:view',
            'tour:book',
            'tour:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:delete',
            'travel_file.passenger:edit',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:cancel',
            'travel_file:close',
            'travel_file:create',
            'travel_file:delete',
            'travel_file:edit',
            'travel_file:reopen',
            'travel_file:view',
            'voucher:cancel',
            'voucher:email',
            'voucher:issue',
            'voucher:view',
        ]
    },

    accounting_clerk: {
        label: 'Accounting',
        description: 'Read travel files & services; full access to financial documents, reconciliation, cashier, payment book, transfer book, and report viewing.',
        category: 'ic',
        // Snapshot of the live "Accounting" role on PROD, 2026-09-23.
        permissions: [
            'agent_invoice:cancel',
            'agent_invoice:email',
            'agent_invoice:issue',
            'agent_reconciliation:edit',
            'agent_reconciliation:view',
            'bsp_reconciliation:edit',
            'bsp_reconciliation:view',
            'cancelled_receipts:edit',
            'cancelled_receipts:view',
            'pelecard_transactions:view',
            'cashier:edit',
            'cashier:view',
            'credit_debit_note:cancel',
            'credit_debit_note:email',
            'credit_debit_note:issue',
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.latest_rates:view',
            'dashboard.open_prqs:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_vouchers:view',
            'dashboard.stats:view',
            'exchange_rate.auto_override:edit',
            'exchange_rate:edit',
            'exchange_rate:view',
            'find_documents:view',
            'general_ledger:export',
            'general_ledger:view',
            'invoice:cancel',
            'invoice:email',
            'invoice:issue',
            'issue_documents:view',
            'payment_book:edit',
            'payment_book:view',
            'prq:cancel',
            'prq:email',
            'prq:issue',
            'prq_approval:view',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'remittance:cancel',
            'remittance:email',
            'remittance:issue',
            'report.cancelled_passengers:export',
            'report.cancelled_passengers:view',
            'report.exchange_rates:export',
            'report.exchange_rates:view',
            'report.prq_rmt:export',
            'report.prq_rmt:view',
            'report.rct_trc:export',
            'report.rct_trc:view',
            'report.reservations:export',
            'report.reservations:view',
            'report.sales:export',
            'report.sales:view',
            'report.supplier_turnover:export',
            'report.supplier_turnover:view',
            'report.supplier_turnover_summary:export',
            'report.supplier_turnover_summary:view',
            'report.tour_details:export',
            'report.tour_details:view',
            'report.tour_registration:export',
            'report.tour_registration:view',
            'report.travel_file_inventory:export',
            'report.travel_file_inventory:view',
            'service.flight:cancel',
            'service.flight:create',
            'service.flight:edit',
            'service.flight:view',
            'service.general:cancel',
            'service.general:create',
            'service.general:edit',
            'service.general:view',
            'service.guide:cancel',
            'service.guide:create',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:cancel',
            'service.hotel:create',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:cancel',
            'service.package:create',
            'service.package:edit',
            'service.package:view',
            'service.tour:cancel',
            'service.tour:create',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:cancel',
            'service.visa:create',
            'service.visa:edit',
            'service.visa:view',
            'setup.bank_account:view',
            'setup.credit_card:view',
            'setup.currency:view',
            'setup.general_account:view',
            'setup.group_travel_file:view',
            'setup.income_account:view',
            'setup.supplier_account:view',
            'setup.travel_agency_account:view',
            'supplier_reconciliation:edit',
            'supplier_reconciliation:view',
            'tax_invoice:cancel',
            'tax_invoice:email',
            'tax_invoice:issue',
            'tour:view',
            'transfer_book:edit',
            'transfer_book:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:view',
            'travel_file.passenger:view',
            'travel_file.rooming:view',
            'travel_file:view',
            'voucher:cancel',
            'voucher:email',
            'voucher:issue',
            'voucher:view',
        ]
    },

    accounting_clerk_senior: {
        label: 'Accounting Senior',
        description: 'Accounting Clerk + sees financial dashboard widgets (revenue, open PRQs, rates, recent vouchers), exports reports and General Ledger, and can edit exchange rates.',
        category: 'ic',
        // Snapshot of the live "Accounting Senior" role on PROD, 2026-09-23.
        permissions: [
            'agent_invoice:cancel',
            'agent_invoice:email',
            'agent_invoice:issue',
            'agent_reconciliation:edit',
            'agent_reconciliation:view',
            'bsp_reconciliation:edit',
            'bsp_reconciliation:view',
            'cancelled_receipts:edit',
            'cancelled_receipts:view',
            'pelecard_transactions:view',
            'cashier:edit',
            'cashier:view',
            'credit_debit_note:cancel',
            'credit_debit_note:email',
            'credit_debit_note:issue',
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.latest_rates:view',
            'dashboard.open_prqs:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.recent_vouchers:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'exchange_rate.auto_override:edit',
            'exchange_rate:edit',
            'exchange_rate:view',
            'find_documents:view',
            'general_ledger:export',
            'general_ledger:view',
            'invoice:cancel',
            'invoice:email',
            'invoice:issue',
            'issue_documents:view',
            'payment_book:edit',
            'payment_book:view',
            'prq:cancel',
            'prq:email',
            'prq:issue',
            'prq_approval:view',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'remittance:cancel',
            'remittance:email',
            'remittance:issue',
            'report.cancelled_passengers:export',
            'report.cancelled_passengers:view',
            'report.documents:export',
            'report.documents:view',
            'report.exchange_rates:export',
            'report.exchange_rates:view',
            'report.left_to_pay:export',
            'report.left_to_pay:view',
            'report.prq_rmt:export',
            'report.prq_rmt:view',
            'report.rct_trc:export',
            'report.rct_trc:view',
            'report.reservations:export',
            'report.reservations:view',
            'report.sales:export',
            'report.sales:view',
            'report.supplier_turnover:export',
            'report.supplier_turnover:view',
            'report.supplier_turnover_summary:export',
            'report.supplier_turnover_summary:view',
            'report.tour_details:export',
            'report.tour_details:view',
            'report.tour_registration:export',
            'report.tour_registration:view',
            'report.travel_file_inventory:export',
            'report.travel_file_inventory:view',
            'service.flight:create',
            'service.flight:edit',
            'service.flight:view',
            'service.general:create',
            'service.general:edit',
            'service.general:view',
            'service.guide:create',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:create',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:create',
            'service.package:edit',
            'service.package:view',
            'service.tour:create',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:create',
            'service.visa:edit',
            'service.visa:view',
            'setup.bank_account:view',
            'setup.credit_card:view',
            'setup.currency:view',
            'setup.general_account:view',
            'setup.group_travel_file:view',
            'setup.income_account:view',
            'setup.supplier_account:view',
            'setup.travel_agency_account:view',
            'supplier_reconciliation:edit',
            'supplier_reconciliation:view',
            'tax_invoice:cancel',
            'tax_invoice:email',
            'tax_invoice:issue',
            'tour:view',
            'transfer_book:edit',
            'transfer_book:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:view',
            'travel_file.passenger:view',
            'travel_file.rooming:view',
            'travel_file:view',
            'voucher:cancel',
            'voucher:email',
            'voucher:issue',
            'voucher:view',
        ]
    },

    marketing: {
        label: 'Marketing',
        description: 'Marketing team: edits tour content / hotels / destinations, manages CRM and email engagements, reads sales/revenue reports for campaign measurement. No financial document editing or travel file editing.',
        category: 'ic',
        // Snapshot of the live "Marketing" role on PROD, 2026-09-23.
        permissions: [
            'dashboard.latest_rates:view',
            'dashboard.open_prqs:view',
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.recent_vouchers:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'report.exchange_rates:export',
            'report.exchange_rates:view',
            'report.prq_rmt:export',
            'report.prq_rmt:view',
            'report.rct_trc:export',
            'report.rct_trc:view',
            'report.reservations:export',
            'report.reservations:view',
            'report.sales:export',
            'report.sales:view',
            'report.supplier_turnover:export',
            'report.supplier_turnover:view',
            'report.supplier_turnover_summary:export',
            'report.supplier_turnover_summary:view',
            'report.tour_details:export',
            'report.tour_details:view',
            'report.tour_registration:export',
            'report.tour_registration:view',
            'report.travel_file_inventory:export',
            'report.travel_file_inventory:view',
            'service.flight:view',
            'service.general:view',
            'service.guide:view',
            'service.hotel:view',
            'service.package:view',
            'service.tour:view',
            'service.visa:view',
            'setup.contract:view',
            'setup.country:view',
            'setup.crm_action:edit',
            'setup.crm_action:view',
            'setup.crm_type:edit',
            'setup.crm_type:view',
            'setup.destination:edit',
            'setup.destination:view',
            'setup.email_engagement:edit',
            'setup.email_engagement:view',
            'setup.escorted_tour:edit',
            'setup.escorted_tour:view',
            'setup.group_travel_file:view',
            'setup.hotel:edit',
            'setup.hotel:view',
            'setup.market:edit',
            'setup.market:view',
            'setup.passenger:view',
            'setup.travel_agency_account:view',
            'tour:view',
            'travel_file.history:view',
            'travel_file.passenger:view',
            'travel_file:view',
            'voucher:view',
        ]
    },

    service: {
        label: 'Service',
        description: 'Customer service desk: views travel files and documents, sends documents to clients by email, adds notes for tracking conversations. No editing.',
        category: 'ic',
        // Snapshot of the live "Service" role on PROD, 2026-09-23.
        permissions: [
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.passengers_chart:view',
            'dashboard.recent_files:view',
            'dashboard.recent_vouchers:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'service.flight:view',
            'service.general:view',
            'service.guide:view',
            'service.hotel:view',
            'service.package:view',
            'service.tour:view',
            'service.visa:view',
            'setup.escorted_tour:view',
            'setup.group_travel_file:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:delete',
            'travel_file.passenger:edit',
            'travel_file.passenger:email',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:close',
            'travel_file:create',
            'travel_file:edit',
            'travel_file:reopen',
            'travel_file:view',
        ]
    },

    service_senior: {
        label: 'Service Senior',
        description: 'Service + edits passengers, issues refunds/receipts/credit notes, sees financial dashboard widgets.',
        category: 'ic',
        // Snapshot of the live "Service Senior" role on PROD, 2026-09-23.
        permissions: [
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'invoice:cancel',
            'invoice:email',
            'invoice:issue',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'report.cancelled_passengers:export',
            'report.cancelled_passengers:view',
            'report.left_to_pay:export',
            'report.left_to_pay:view',
            'report.prq_rmt:export',
            'report.prq_rmt:view',
            'report.rct_trc:export',
            'report.rct_trc:view',
            'report.reservations:export',
            'report.reservations:view',
            'report.sales:export',
            'report.sales:view',
            'service.flight:cancel',
            'service.flight:create',
            'service.flight:delete',
            'service.flight:edit',
            'service.flight:view',
            'service.general:cancel',
            'service.general:create',
            'service.general:delete',
            'service.general:edit',
            'service.general:view',
            'service.guide:cancel',
            'service.guide:create',
            'service.guide:delete',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:cancel',
            'service.hotel:create',
            'service.hotel:delete',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:cancel',
            'service.package:create',
            'service.package:delete',
            'service.package:edit',
            'service.package:view',
            'service.tour:cancel',
            'service.tour:create',
            'service.tour:delete',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:cancel',
            'service.visa:create',
            'service.visa:delete',
            'service.visa:edit',
            'service.visa:view',
            'setup.escorted_tour:view',
            'setup.group_travel_file:view',
            'tax_invoice:cancel',
            'tax_invoice:email',
            'tax_invoice:issue',
            'tour:book',
            'tour:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:delete',
            'travel_file.passenger:edit',
            'travel_file.passenger:email',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:cancel',
            'travel_file:close',
            'travel_file:create',
            'travel_file:delete',
            'travel_file:edit',
            'travel_file:reopen',
            'travel_file:view',
            'voucher:view',
        ]
    },

    sales_vp: {
        label: 'Sales VP',
        description: 'VP of Sales: full Sales + Operator capabilities, plus cancel docs, close/reopen files, all reports with export, sales-related setup management.',
        category: 'department',
        // Snapshot of the live "Sales VP" role on PROD, 2026-09-23.
        permissions: [
            'agent_invoice:cancel',
            'agent_invoice:email',
            'agent_invoice:issue',
            'credit_debit_note:cancel',
            'credit_debit_note:email',
            'credit_debit_note:issue',
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.latest_rates:view',
            'dashboard.open_prqs:view',
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.recent_vouchers:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'invoice:cancel',
            'invoice:email',
            'invoice:issue',
            'prq:cancel',
            'prq:email',
            'prq:issue',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'remittance:cancel',
            'remittance:email',
            'remittance:issue',
            'report.cancelled_passengers:export',
            'report.cancelled_passengers:view',
            'report.exchange_rates:export',
            'report.exchange_rates:view',
            'report.left_to_pay:export',
            'report.left_to_pay:view',
            'report.prq_rmt:export',
            'report.prq_rmt:view',
            'report.rct_trc:export',
            'report.rct_trc:view',
            'report.reservations:export',
            'report.reservations:view',
            'report.sales:export',
            'report.sales:view',
            'report.supplier_turnover:export',
            'report.supplier_turnover:view',
            'report.supplier_turnover_summary:export',
            'report.supplier_turnover_summary:view',
            'report.tour_details:export',
            'report.tour_details:view',
            'report.tour_registration:export',
            'report.tour_registration:view',
            'report.travel_file_inventory:export',
            'report.travel_file_inventory:view',
            'service.flight:cancel',
            'service.flight:create',
            'service.flight:delete',
            'service.flight:edit',
            'service.flight:view',
            'service.general:cancel',
            'service.general:create',
            'service.general:delete',
            'service.general:edit',
            'service.general:view',
            'service.guide:cancel',
            'service.guide:create',
            'service.guide:delete',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:cancel',
            'service.hotel:create',
            'service.hotel:delete',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:cancel',
            'service.package:create',
            'service.package:delete',
            'service.package:edit',
            'service.package:view',
            'service.tour:cancel',
            'service.tour:create',
            'service.tour:delete',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:cancel',
            'service.visa:create',
            'service.visa:delete',
            'service.visa:edit',
            'service.visa:view',
            'setup.escorted_tour:edit',
            'setup.escorted_tour:view',
            'setup.general_info:view',
            'setup.group_travel_file:view',
            'setup.market:edit',
            'setup.market:view',
            'setup.supplier_account:view',
            'setup.travel_agency_account:view',
            'setup.travel_agent_type:view',
            'tax_invoice:cancel',
            'tax_invoice:email',
            'tax_invoice:issue',
            'tour:book',
            'tour:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:delete',
            'travel_file.passenger:edit',
            'travel_file.passenger:email',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:cancel',
            'travel_file:close',
            'travel_file:create',
            'travel_file:edit',
            'travel_file:reopen',
            'travel_file:view',
            'voucher:cancel',
            'voucher:email',
            'voucher:issue',
            'voucher:view',
        ]
    },

    operations_vp: {
        label: 'Operations VP',
        description: 'VP of Operations: full operator capabilities + setup management, full dashboard, all reports with export.',
        category: 'department',
        // Snapshot of the live "Operations VP" role on PROD, 2026-09-23.
        permissions: [
            'agent_invoice:cancel',
            'agent_invoice:email',
            'agent_invoice:issue',
            'credit_debit_note:cancel',
            'credit_debit_note:email',
            'credit_debit_note:issue',
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.latest_rates:view',
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.recent_vouchers:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'invoice:cancel',
            'invoice:email',
            'invoice:issue',
            'prq:cancel',
            'prq:email',
            'prq:issue',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'remittance:cancel',
            'remittance:email',
            'remittance:issue',
            'report.cancelled_passengers:export',
            'report.cancelled_passengers:view',
            'report.exchange_rates:export',
            'report.exchange_rates:view',
            'report.reservations:export',
            'report.reservations:view',
            'report.sales:export',
            'report.sales:view',
            'report.supplier_turnover:export',
            'report.supplier_turnover:view',
            'report.supplier_turnover_summary:export',
            'report.supplier_turnover_summary:view',
            'report.tour_details:export',
            'report.tour_details:view',
            'report.tour_registration:export',
            'report.tour_registration:view',
            'report.travel_file_inventory:export',
            'report.travel_file_inventory:view',
            'service.flight:cancel',
            'service.flight:create',
            'service.flight:delete',
            'service.flight:edit',
            'service.flight:view',
            'service.general:cancel',
            'service.general:create',
            'service.general:delete',
            'service.general:edit',
            'service.general:view',
            'service.guide:cancel',
            'service.guide:create',
            'service.guide:delete',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:cancel',
            'service.hotel:create',
            'service.hotel:delete',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:cancel',
            'service.package:create',
            'service.package:delete',
            'service.package:edit',
            'service.package:view',
            'service.tour:cancel',
            'service.tour:create',
            'service.tour:delete',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:cancel',
            'service.visa:create',
            'service.visa:delete',
            'service.visa:edit',
            'service.visa:view',
            'setup.escorted_tour:edit',
            'setup.escorted_tour:view',
            'setup.group_travel_file:view',
            'setup.hotel:edit',
            'setup.hotel:view',
            'setup.hotel_facility:edit',
            'setup.hotel_facility:view',
            'tax_invoice:cancel',
            'tax_invoice:email',
            'tax_invoice:issue',
            'tour:book',
            'tour:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:delete',
            'travel_file.passenger:edit',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:cancel',
            'travel_file:close',
            'travel_file:create',
            'travel_file:delete',
            'travel_file:edit',
            'travel_file:reopen',
            'travel_file:view',
            'voucher:cancel',
            'voucher:email',
            'voucher:issue',
            'voucher:view',
        ]
    },

    aviation_manager: {
        label: 'Aviation Management',
        description: 'Runs the Aviation desk: everything an Aviation Team Member does, plus the file-level lifecycle (close, reopen, balance, delete) and the full reports/dashboard set with export.',
        category: 'department',
        // Snapshot of the live "Aviation Management" role on PROD, 2026-09-23.
        permissions: [
            'dashboard.latest_rates:view',
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.recent_vouchers:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'report.tour_details:export',
            'report.tour_details:view',
            'service.flight:cancel',
            'service.flight:create',
            'service.flight:delete',
            'service.flight:edit',
            'service.flight:view',
            'service.general:cancel',
            'service.general:create',
            'service.general:delete',
            'service.general:edit',
            'service.general:view',
            'service.guide:cancel',
            'service.guide:create',
            'service.guide:delete',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:cancel',
            'service.hotel:create',
            'service.hotel:delete',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:cancel',
            'service.package:create',
            'service.package:delete',
            'service.package:edit',
            'service.package:view',
            'service.tour:cancel',
            'service.tour:create',
            'service.tour:delete',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:cancel',
            'service.visa:create',
            'service.visa:delete',
            'service.visa:edit',
            'service.visa:view',
            'setup.escorted_tour:edit',
            'setup.escorted_tour:view',
            'setup.group_travel_file:view',
            'tour:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:edit',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:cancel',
            'travel_file:close',
            'travel_file:create',
            'travel_file:edit',
            'travel_file:reopen',
            'travel_file:view',
            'voucher:cancel',
            'voucher:email',
            'voucher:issue',
            'voucher:view',
        ]
    },

    accounting_vp: {
        label: 'Accounting VP',
        description: 'VP of Accounting: full accounting + financial workflows, manages financial setup, full reports/export, full financial dashboard.',
        category: 'department',
        // Snapshot of the live "Accounting VP" role on PROD, 2026-09-23.
        permissions: [
            'agent_invoice:cancel',
            'agent_invoice:email',
            'agent_invoice:issue',
            'agent_reconciliation:edit',
            'agent_reconciliation:view',
            'bsp_reconciliation:edit',
            'bsp_reconciliation:view',
            'cancelled_receipts:edit',
            'cancelled_receipts:view',
            'pelecard_transactions:view',
            'cashier:edit',
            'cashier:view',
            'credit_debit_note:cancel',
            'credit_debit_note:email',
            'credit_debit_note:issue',
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.latest_rates:view',
            'dashboard.open_prqs:view',
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.recent_vouchers:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'exchange_rate.auto_override:edit',
            'exchange_rate.limit:edit',
            'exchange_rate:edit',
            'exchange_rate:view',
            'find_documents:view',
            'general_ledger:export',
            'general_ledger:view',
            'invoice:cancel',
            'invoice:email',
            'invoice:issue',
            'issue_documents:view',
            'payment_book:edit',
            'payment_book:view',
            'prq:approve',
            'prq:cancel',
            'prq:email',
            'prq:issue',
            'prq_approval:view',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'remittance:cancel',
            'remittance:email',
            'remittance:issue',
            'report.cancelled_passengers:export',
            'report.cancelled_passengers:view',
            'report.documents:export',
            'report.documents:view',
            'report.exchange_rates:export',
            'report.exchange_rates:view',
            'report.left_to_pay:export',
            'report.left_to_pay:view',
            'report.prq_rmt:export',
            'report.prq_rmt:view',
            'report.rct_trc:export',
            'report.rct_trc:view',
            'report.reservations:export',
            'report.reservations:view',
            'report.sales:export',
            'report.sales:view',
            'report.supplier_turnover:export',
            'report.supplier_turnover:view',
            'report.supplier_turnover_summary:export',
            'report.supplier_turnover_summary:view',
            'report.tour_details:export',
            'report.tour_details:view',
            'report.tour_registration:export',
            'report.tour_registration:view',
            'report.travel_file_inventory:export',
            'report.travel_file_inventory:view',
            'service.flight:cancel',
            'service.flight:create',
            'service.flight:edit',
            'service.flight:view',
            'service.general:cancel',
            'service.general:create',
            'service.general:edit',
            'service.general:view',
            'service.guide:cancel',
            'service.guide:create',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:cancel',
            'service.hotel:create',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:cancel',
            'service.package:create',
            'service.package:edit',
            'service.package:view',
            'service.tour:cancel',
            'service.tour:create',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:cancel',
            'service.visa:create',
            'service.visa:edit',
            'service.visa:view',
            'setup.agent_commission_account:edit',
            'setup.agent_commission_account:view',
            'setup.bank_account:edit',
            'setup.bank_account:view',
            'setup.credit_card:edit',
            'setup.credit_card:view',
            'setup.credit_company:edit',
            'setup.credit_company:view',
            'setup.credit_payment_option:edit',
            'setup.credit_payment_option:view',
            'setup.currency:edit',
            'setup.currency:view',
            'setup.discount_account:view',
            'setup.document_approval:edit',
            'setup.document_approval:view',
            'setup.document_type_numbering:edit',
            'setup.document_type_numbering:view',
            'setup.event:edit',
            'setup.event:view',
            'setup.general_account:edit',
            'setup.general_account:view',
            'setup.group_travel_file:view',
            'setup.income_account:edit',
            'setup.income_account:view',
            'setup.income_type:edit',
            'setup.income_type:view',
            'setup.payments_terminal:view',
            'setup.supplier_account:view',
            'setup.travel_agency_account:view',
            'supplier_reconciliation:edit',
            'supplier_reconciliation:view',
            'tax_invoice:cancel',
            'tax_invoice:email',
            'tax_invoice:issue',
            'tour:view',
            'transfer_book:edit',
            'transfer_book:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:view',
            'travel_file.passenger:view',
            'travel_file.rooming:view',
            'travel_file:view',
            'voucher:cancel',
            'voucher:email',
            'voucher:issue',
            'voucher:view',
        ]
    },

    service_vp: {
        label: 'Service VP',
        description: 'VP of Service: full Service Senior + edits travel files and notes, full operational reports/dashboard.',
        category: 'department',
        // Snapshot of the live "Service VP" role on PROD, 2026-09-23.
        permissions: [
            'agent_invoice:email',
            'credit_debit_note:cancel',
            'credit_debit_note:email',
            'credit_debit_note:issue',
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.latest_rates:view',
            'dashboard.open_prqs:view',
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.recent_vouchers:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'invoice:email',
            'prq:email',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'remittance:email',
            'report.cancelled_passengers:export',
            'report.cancelled_passengers:view',
            'report.exchange_rates:export',
            'report.exchange_rates:view',
            'report.prq_rmt:export',
            'report.prq_rmt:view',
            'report.rct_trc:export',
            'report.rct_trc:view',
            'report.reservations:export',
            'report.reservations:view',
            'report.sales:export',
            'report.sales:view',
            'report.supplier_turnover:export',
            'report.supplier_turnover:view',
            'report.supplier_turnover_summary:export',
            'report.supplier_turnover_summary:view',
            'report.tour_details:export',
            'report.tour_details:view',
            'report.tour_registration:export',
            'report.tour_registration:view',
            'report.travel_file_inventory:export',
            'report.travel_file_inventory:view',
            'service.flight:cancel',
            'service.flight:create',
            'service.flight:delete',
            'service.flight:edit',
            'service.flight:view',
            'service.general:cancel',
            'service.general:create',
            'service.general:delete',
            'service.general:edit',
            'service.general:view',
            'service.guide:cancel',
            'service.guide:create',
            'service.guide:delete',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:cancel',
            'service.hotel:create',
            'service.hotel:delete',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:cancel',
            'service.package:create',
            'service.package:delete',
            'service.package:edit',
            'service.package:view',
            'service.tour:cancel',
            'service.tour:create',
            'service.tour:delete',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:cancel',
            'service.visa:create',
            'service.visa:delete',
            'service.visa:edit',
            'service.visa:view',
            'setup.country:view',
            'setup.destination:view',
            'setup.escorted_tour:view',
            'setup.group_travel_file:view',
            'setup.hotel:view',
            'setup.travel_agency_account:view',
            'tax_invoice:email',
            'tour:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:delete',
            'travel_file.passenger:edit',
            'travel_file.passenger:email',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:cancel',
            'travel_file:close',
            'travel_file:create',
            'travel_file:delete',
            'travel_file:edit',
            'travel_file:reopen',
            'travel_file:view',
            'voucher:email',
            'voucher:view',
        ]
    },

    management: {
        label: 'Management',
        description: 'Cross-department head: union of all 4 VP roles (Sales, Operations, Accounting, Service). Manages setup data across departments, full reports + dashboard, but cannot cancel financial documents or delete travel files (those stay with General Manager) and no admin/system rights.',
        category: 'company',
        // Snapshot of the live "Management" role on PROD, 2026-09-23.
        permissions: [
            'agent_invoice:cancel',
            'agent_invoice:email',
            'agent_invoice:issue',
            'agent_reconciliation:edit',
            'agent_reconciliation:view',
            'bsp_reconciliation:edit',
            'bsp_reconciliation:view',
            'cancelled_receipts:edit',
            'cancelled_receipts:view',
            'pelecard_transactions:view',
            'cashier:edit',
            'cashier:view',
            'credit_debit_note:cancel',
            'credit_debit_note:email',
            'credit_debit_note:issue',
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.latest_rates:view',
            'dashboard.open_prqs:view',
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.recent_vouchers:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'exchange_rate:edit',
            'exchange_rate:view',
            'find_documents:view',
            'general_ledger:export',
            'general_ledger:view',
            'invoice:cancel',
            'invoice:email',
            'invoice:issue',
            'issue_documents:view',
            'payment_book:edit',
            'payment_book:view',
            'prq:cancel',
            'prq:email',
            'prq:issue',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'remittance:cancel',
            'remittance:email',
            'remittance:issue',
            'report.cancelled_passengers:export',
            'report.cancelled_passengers:view',
            'report.exchange_rates:export',
            'report.exchange_rates:view',
            'report.prq_rmt:export',
            'report.prq_rmt:view',
            'report.rct_trc:export',
            'report.rct_trc:view',
            'report.reservations:export',
            'report.reservations:view',
            'report.sales:export',
            'report.sales:view',
            'report.supplier_turnover:export',
            'report.supplier_turnover:view',
            'report.supplier_turnover_summary:export',
            'report.supplier_turnover_summary:view',
            'report.tour_details:export',
            'report.tour_details:view',
            'report.tour_registration:export',
            'report.tour_registration:view',
            'report.travel_file_inventory:export',
            'report.travel_file_inventory:view',
            'service.flight:create',
            'service.flight:edit',
            'service.flight:view',
            'service.general:create',
            'service.general:edit',
            'service.general:view',
            'service.guide:create',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:create',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:create',
            'service.package:edit',
            'service.package:view',
            'service.tour:create',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:create',
            'service.visa:edit',
            'service.visa:view',
            'setup.agent_commission_account:edit',
            'setup.agent_commission_account:view',
            'setup.bank_account:edit',
            'setup.bank_account:view',
            'setup.category:edit',
            'setup.category:view',
            'setup.class:edit',
            'setup.class:view',
            'setup.component_level:edit',
            'setup.component_level:view',
            'setup.component_type:edit',
            'setup.component_type:view',
            'setup.contract:edit',
            'setup.contract:view',
            'setup.country:edit',
            'setup.country:view',
            'setup.credit_card:edit',
            'setup.credit_card:view',
            'setup.credit_company:edit',
            'setup.credit_company:view',
            'setup.credit_payment_option:edit',
            'setup.credit_payment_option:view',
            'setup.crm_action:edit',
            'setup.crm_action:view',
            'setup.crm_type:edit',
            'setup.crm_type:view',
            'setup.currency:edit',
            'setup.currency:view',
            'setup.destination:edit',
            'setup.destination:view',
            'setup.discount_account:edit',
            'setup.discount_account:view',
            'setup.document_type_numbering:edit',
            'setup.document_type_numbering:view',
            'setup.email_engagement:edit',
            'setup.email_engagement:view',
            'setup.escorted_tour:edit',
            'setup.escorted_tour:view',
            'setup.event:edit',
            'setup.event:view',
            'setup.general_account:edit',
            'setup.general_account:view',
            'setup.general_info:edit',
            'setup.general_info:view',
            'setup.group_travel_file:view',
            'setup.guide_language:edit',
            'setup.guide_language:view',
            'setup.hotel:edit',
            'setup.hotel:view',
            'setup.hotel_facility:edit',
            'setup.hotel_facility:view',
            'setup.income_account:edit',
            'setup.income_account:view',
            'setup.income_type:edit',
            'setup.income_type:view',
            'setup.location:edit',
            'setup.location:view',
            'setup.market:edit',
            'setup.market:view',
            'setup.passenger:edit',
            'setup.passenger:view',
            'setup.passenger_attribute:edit',
            'setup.passenger_attribute:view',
            'setup.payment_option:edit',
            'setup.payment_option:view',
            'setup.price_component:edit',
            'setup.price_component:view',
            'setup.price_level:edit',
            'setup.price_level:view',
            'setup.queue:edit',
            'setup.queue:view',
            'setup.report_reservation_supplier:view',
            'setup.service_abbreviation:edit',
            'setup.service_abbreviation:view',
            'setup.service_attribute:edit',
            'setup.service_attribute:view',
            'setup.service_type:edit',
            'setup.service_type:view',
            'setup.supplier_account:edit',
            'setup.supplier_account:view',
            'setup.supplier_interface:edit',
            'setup.supplier_interface:view',
            'setup.travel_agency_account:edit',
            'setup.travel_agency_account:view',
            'setup.travel_agent_type:edit',
            'setup.travel_agent_type:view',
            'setup.travel_file_attribute:edit',
            'setup.travel_file_attribute:view',
            'setup.update_reservation_supplier:view',
            'supplier_reconciliation:edit',
            'supplier_reconciliation:view',
            'tax_invoice:cancel',
            'tax_invoice:email',
            'tax_invoice:issue',
            'tour:book',
            'tour:view',
            'transfer_book:edit',
            'transfer_book:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:delete',
            'travel_file.passenger:edit',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:create',
            'travel_file:edit',
            'travel_file:view',
            'voucher:cancel',
            'voucher:email',
            'voucher:issue',
            'voucher:view',
        ]
    },

    general_manager: {
        label: 'General Manager',
        description: 'Full operational and financial access, including all reports and setup. Excluded from user/role administration and low-level system tools.',
        category: 'company',
        // Snapshot of the live "General Manager" role on PROD, 2026-09-23.
        permissions: [
            'agent_invoice:cancel',
            'agent_invoice:email',
            'agent_invoice:issue',
            'agent_reconciliation:edit',
            'agent_reconciliation:view',
            'bsp_reconciliation:edit',
            'bsp_reconciliation:view',
            'cancelled_receipts:edit',
            'cancelled_receipts:view',
            'pelecard_transactions:view',
            'cashier:edit',
            'cashier:view',
            'credit_debit_note:cancel',
            'credit_debit_note:email',
            'credit_debit_note:issue',
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.latest_rates:view',
            'dashboard.open_prqs:view',
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.recent_vouchers:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'exchange_rate:edit',
            'exchange_rate:view',
            'find_documents:view',
            'general_ledger:export',
            'general_ledger:view',
            'invoice:cancel',
            'invoice:email',
            'invoice:issue',
            'issue_documents:view',
            'payment_book:edit',
            'payment_book:view',
            'prq:approve',
            'prq:cancel',
            'prq:email',
            'prq:issue',
            'prq_approval:view',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'remittance:cancel',
            'remittance:email',
            'remittance:issue',
            'report.cancelled_passengers:export',
            'report.cancelled_passengers:view',
            'report.exchange_rates:export',
            'report.exchange_rates:view',
            'report.prq_rmt:export',
            'report.prq_rmt:view',
            'report.rct_trc:export',
            'report.rct_trc:view',
            'report.reservations:export',
            'report.reservations:view',
            'report.sales:export',
            'report.sales:view',
            'report.supplier_turnover:export',
            'report.supplier_turnover:view',
            'report.supplier_turnover_summary:export',
            'report.supplier_turnover_summary:view',
            'report.tour_details:export',
            'report.tour_details:view',
            'report.tour_registration:export',
            'report.tour_registration:view',
            'report.travel_file_inventory:export',
            'report.travel_file_inventory:view',
            'service.flight:cancel',
            'service.flight:create',
            'service.flight:delete',
            'service.flight:edit',
            'service.flight:view',
            'service.general:cancel',
            'service.general:create',
            'service.general:delete',
            'service.general:edit',
            'service.general:view',
            'service.guide:cancel',
            'service.guide:create',
            'service.guide:delete',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:cancel',
            'service.hotel:create',
            'service.hotel:delete',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:cancel',
            'service.package:create',
            'service.package:delete',
            'service.package:edit',
            'service.package:view',
            'service.tour:cancel',
            'service.tour:create',
            'service.tour:delete',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:cancel',
            'service.visa:create',
            'service.visa:delete',
            'service.visa:edit',
            'service.visa:view',
            'setup.agent_commission_account:edit',
            'setup.agent_commission_account:view',
            'setup.bank_account:edit',
            'setup.bank_account:view',
            'setup.category:edit',
            'setup.category:view',
            'setup.class:edit',
            'setup.class:view',
            'setup.component_level:edit',
            'setup.component_level:view',
            'setup.component_type:edit',
            'setup.component_type:view',
            'setup.contract:edit',
            'setup.contract:view',
            'setup.country:edit',
            'setup.country:view',
            'setup.credit_card:edit',
            'setup.credit_card:view',
            'setup.credit_company:edit',
            'setup.credit_company:view',
            'setup.credit_payment_option:edit',
            'setup.credit_payment_option:view',
            'setup.crm_action:edit',
            'setup.crm_action:view',
            'setup.crm_type:edit',
            'setup.crm_type:view',
            'setup.currency:edit',
            'setup.currency:view',
            'setup.destination:edit',
            'setup.destination:view',
            'setup.discount_account:edit',
            'setup.discount_account:view',
            'setup.document_approval:edit',
            'setup.document_approval:view',
            'setup.document_type_numbering:edit',
            'setup.document_type_numbering:view',
            'setup.email_engagement:edit',
            'setup.email_engagement:view',
            'setup.escorted_tour:edit',
            'setup.escorted_tour:view',
            'setup.event:edit',
            'setup.event:view',
            'setup.general_account:edit',
            'setup.general_account:view',
            'setup.general_info:edit',
            'setup.general_info:view',
            'setup.group_travel_file:view',
            'setup.guide_language:edit',
            'setup.guide_language:view',
            'setup.hotel:edit',
            'setup.hotel:view',
            'setup.hotel_facility:edit',
            'setup.hotel_facility:view',
            'setup.income_account:edit',
            'setup.income_account:view',
            'setup.income_type:edit',
            'setup.income_type:view',
            'setup.location:edit',
            'setup.location:view',
            'setup.market:edit',
            'setup.market:view',
            'setup.passenger:edit',
            'setup.passenger:view',
            'setup.passenger_attribute:edit',
            'setup.passenger_attribute:view',
            'setup.payment_option:edit',
            'setup.payment_option:view',
            'setup.price_component:edit',
            'setup.price_component:view',
            'setup.price_level:edit',
            'setup.price_level:view',
            'setup.queue:edit',
            'setup.queue:view',
            'setup.report_reservation_supplier:view',
            'setup.service_abbreviation:edit',
            'setup.service_abbreviation:view',
            'setup.service_attribute:edit',
            'setup.service_attribute:view',
            'setup.service_type:edit',
            'setup.service_type:view',
            'setup.supplier_account:edit',
            'setup.supplier_account:view',
            'setup.supplier_interface:edit',
            'setup.supplier_interface:view',
            'setup.travel_agency_account:edit',
            'setup.travel_agency_account:view',
            'setup.travel_agent_type:edit',
            'setup.travel_agent_type:view',
            'setup.travel_file_attribute:edit',
            'setup.travel_file_attribute:view',
            'setup.update_reservation_supplier:view',
            'supplier_reconciliation:edit',
            'supplier_reconciliation:view',
            'tax_invoice:cancel',
            'tax_invoice:email',
            'tax_invoice:issue',
            'tour:book',
            'tour:view',
            'transfer_book:edit',
            'transfer_book:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:delete',
            'travel_file.passenger:edit',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:cancel',
            'travel_file:close',
            'travel_file:create',
            'travel_file:delete',
            'travel_file:edit',
            'travel_file:reopen',
            'travel_file:view',
            'voucher:cancel',
            'voucher:email',
            'voucher:issue',
            'voucher:view',
        ]
    },

    admin: {
        label: 'Administrator',
        description: 'Full access to everything including user/role management and system settings.',
        category: 'company',
        permissions: ['*:*']
    },

    system_admin: {
        label: 'System Admin',
        description: 'Systems administrator: everything Administrator has, plus user/role management and the system tools.',
        category: 'company',
        // Snapshot of the live "System Admin" role on PROD, 2026-09-23.
        permissions: [
            'admin.role:edit',
            'admin.role:view',
            'admin.user:edit',
            'admin.user:view',
            'agent_invoice:cancel',
            'agent_invoice:email',
            'agent_invoice:issue',
            'agent_reconciliation:edit',
            'agent_reconciliation:view',
            'bsp_reconciliation:edit',
            'bsp_reconciliation:view',
            'cancelled_receipts:edit',
            'cancelled_receipts:view',
            'pelecard_transactions:view',
            'cashier:edit',
            'cashier:view',
            'credit_debit_note:cancel',
            'credit_debit_note:email',
            'credit_debit_note:issue',
            'customer_card:charge',
            'customer_card:delete',
            'customer_card:view',
            'dashboard.latest_rates:view',
            'dashboard.open_prqs:view',
            'dashboard.passengers_chart:view',
            'dashboard.quick_actions:view',
            'dashboard.recent_files:view',
            'dashboard.recent_vouchers:view',
            'dashboard.stats:view',
            'dashboard.travel_files_chart:view',
            'dashboard.upcoming_departures:view',
            'exchange_rate.auto_override:edit',
            'exchange_rate.limit:edit',
            'exchange_rate:edit',
            'exchange_rate:view',
            'find_documents:view',
            'general_ledger:export',
            'general_ledger:view',
            'invoice:cancel',
            'invoice:email',
            'invoice:issue',
            'issue_documents:view',
            'payment_book:edit',
            'payment_book:view',
            'prq:approve',
            'prq:cancel',
            'prq:email',
            'prq:issue',
            'prq_approval:view',
            'receipt:cancel',
            'receipt:email',
            'receipt:issue',
            'refund:cancel',
            'refund:email',
            'refund:issue',
            'remittance:cancel',
            'remittance:email',
            'remittance:issue',
            'report.cancelled_passengers:export',
            'report.cancelled_passengers:view',
            'report.documents:export',
            'report.documents:view',
            'report.exchange_rates:export',
            'report.exchange_rates:view',
            'report.left_to_pay:export',
            'report.left_to_pay:view',
            'report.prq_rmt:export',
            'report.prq_rmt:view',
            'report.rct_trc:export',
            'report.rct_trc:view',
            'report.reservations:export',
            'report.reservations:view',
            'report.sales:export',
            'report.sales:view',
            'report.supplier_turnover:export',
            'report.supplier_turnover:view',
            'report.supplier_turnover_summary:export',
            'report.supplier_turnover_summary:view',
            'report.tour_details:export',
            'report.tour_details:view',
            'report.tour_registration:export',
            'report.tour_registration:view',
            'report.travel_file_inventory:export',
            'report.travel_file_inventory:view',
            'service.flight:cancel',
            'service.flight:create',
            'service.flight:delete',
            'service.flight:edit',
            'service.flight:view',
            'service.general:cancel',
            'service.general:create',
            'service.general:delete',
            'service.general:edit',
            'service.general:view',
            'service.guide:cancel',
            'service.guide:create',
            'service.guide:delete',
            'service.guide:edit',
            'service.guide:view',
            'service.hotel:cancel',
            'service.hotel:create',
            'service.hotel:delete',
            'service.hotel:edit',
            'service.hotel:view',
            'service.package:cancel',
            'service.package:create',
            'service.package:delete',
            'service.package:edit',
            'service.package:view',
            'service.tour:cancel',
            'service.tour:create',
            'service.tour:delete',
            'service.tour:edit',
            'service.tour:view',
            'service.visa:cancel',
            'service.visa:create',
            'service.visa:delete',
            'service.visa:edit',
            'service.visa:view',
            'setup.agent_commission_account:edit',
            'setup.agent_commission_account:view',
            'setup.bank_account:edit',
            'setup.bank_account:view',
            'setup.category:edit',
            'setup.category:view',
            'setup.class:edit',
            'setup.class:view',
            'setup.component_level:edit',
            'setup.component_level:view',
            'setup.component_type:edit',
            'setup.component_type:view',
            'setup.contract:edit',
            'setup.contract:view',
            'setup.country:edit',
            'setup.country:view',
            'setup.credit_card:edit',
            'setup.credit_card:view',
            'setup.credit_company:edit',
            'setup.credit_company:view',
            'setup.credit_payment_option:edit',
            'setup.credit_payment_option:view',
            'setup.crm_action:edit',
            'setup.crm_action:view',
            'setup.crm_type:edit',
            'setup.crm_type:view',
            'setup.currency:edit',
            'setup.currency:view',
            'setup.destination:edit',
            'setup.destination:view',
            'setup.discount_account:edit',
            'setup.discount_account:view',
            'setup.document_approval:edit',
            'setup.document_approval:view',
            'setup.document_type_numbering:edit',
            'setup.document_type_numbering:view',
            'setup.email_engagement:edit',
            'setup.email_engagement:view',
            'setup.email_template:edit',
            'setup.email_template:view',
            'setup.escorted_tour:edit',
            'setup.escorted_tour:view',
            'setup.event:edit',
            'setup.event:view',
            'setup.general_account:edit',
            'setup.general_account:view',
            'setup.general_info:edit',
            'setup.general_info:view',
            'setup.group_travel_file:view',
            'setup.guide_language:edit',
            'setup.guide_language:view',
            'setup.hotel:edit',
            'setup.hotel:view',
            'setup.hotel_facility:edit',
            'setup.hotel_facility:view',
            'setup.income_account:edit',
            'setup.income_account:view',
            'setup.income_type:edit',
            'setup.income_type:view',
            'setup.location:edit',
            'setup.location:view',
            'setup.market:edit',
            'setup.market:view',
            'setup.passenger:edit',
            'setup.passenger:view',
            'setup.passenger_attribute:edit',
            'setup.passenger_attribute:view',
            'setup.payment_option:edit',
            'setup.payment_option:view',
            'setup.payments_terminal:edit',
            'setup.payments_terminal:view',
            'setup.price_component:edit',
            'setup.price_component:view',
            'setup.price_level:edit',
            'setup.price_level:view',
            'setup.queue:edit',
            'setup.queue:view',
            'setup.report_reservation_supplier:view',
            'setup.service_abbreviation:edit',
            'setup.service_abbreviation:view',
            'setup.service_attribute:edit',
            'setup.service_attribute:view',
            'setup.service_type:edit',
            'setup.service_type:view',
            'setup.supplier_account:edit',
            'setup.supplier_account:view',
            'setup.supplier_interface:edit',
            'setup.supplier_interface:view',
            'setup.tour_registration_rule:edit',
            'setup.tour_registration_rule:view',
            'setup.travel_agency_account:edit',
            'setup.travel_agency_account:view',
            'setup.travel_agent_type:edit',
            'setup.travel_agent_type:view',
            'setup.travel_file_attribute:edit',
            'setup.travel_file_attribute:view',
            'setup.update_reservation_supplier:view',
            'supplier_reconciliation:edit',
            'supplier_reconciliation:view',
            'system.amadeus:view',
            'system.app_settings:edit',
            'system.appearance:edit',
            'system.database:view',
            'system.email_settings:edit',
            'system.grid_settings:edit',
            'system.jira:edit',
            'system.signature:view',
            'system.tickets:edit',
            'system.tickets:view',
            'system.upload_users:edit',
            'system.welcome_email:edit',
            'tax_invoice:cancel',
            'tax_invoice:email',
            'tax_invoice:issue',
            'tour:book',
            'tour:view',
            'transfer_book:edit',
            'transfer_book:view',
            'travel_file.accounting:view',
            'travel_file.history:view',
            'travel_file.note:create',
            'travel_file.note:edit',
            'travel_file.note:view',
            'travel_file.passenger:create',
            'travel_file.passenger:delete',
            'travel_file.passenger:edit',
            'travel_file.passenger:email',
            'travel_file.passenger:view',
            'travel_file.rooming:edit',
            'travel_file.rooming:view',
            'travel_file:balance',
            'travel_file:cancel',
            'travel_file:close',
            'travel_file:create',
            'travel_file:delete',
            'travel_file:edit',
            'travel_file:reopen',
            'travel_file:view',
            'voucher:cancel',
            'voucher:email',
            'voucher:issue',
            'voucher:view',
        ]
    }
} satisfies Record<string, Preset>;

export type RolePresetKey = keyof typeof ROLE_PRESETS;
export type { PresetCategory };

/** Display labels for the preset categories — used as section headers in the role editor dropdown. */
export const PRESET_CATEGORY_LABELS: Record<PresetCategory, string> = {
    ic: 'Individual Contributor',
    department: 'Department Head',
    company: 'Company-Wide',
    special: 'Special'
};

/** Order in which preset categories appear in the dropdown. */
export const PRESET_CATEGORY_ORDER: PresetCategory[] = ['special', 'ic', 'department', 'company'];

// ─────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────
export type BuildPerms<T extends string, A extends Action> = Record<`${T}:${A}`, PermissionMeta>;

function makeMeta(label: string, description: string, category: string): PermissionMeta {
    return { label, description, category };
}

//** Build the standard 6 permissions for a service type */
export function buildServicePerms<T extends string>(resource: T, displayName: string) {
    const displayNameLower = displayName.toLowerCase();
    return {
        [`${resource}:view`]: makeMeta(
            `View ${displayName} Service`,
            `See ${displayNameLower} services in travel files`,
            'Travel File Services'
        ),
        [`${resource}:create`]: makeMeta(
            `Create ${displayName} Service`,
            `Add new ${displayNameLower} services to travel files`,
            'Travel File Services'
        ),
        [`${resource}:edit`]: makeMeta(
            `Edit ${displayName} Service`,
            `Modify ${displayNameLower} service details, pricing, accounting`,
            'Travel File Services'
        ),
        [`${resource}:delete`]: makeMeta(
            `Delete ${displayName} Service`,
            `Remove ${displayNameLower} services from travel files`,
            'Travel File Services'
        ),
        [`${resource}:cancel`]: makeMeta(
            `Cancel ${displayName} Service`,
            `Cancel a reservation for a ${displayNameLower} service`,
            'Travel File Services'
        )
    } as BuildPerms<T, 'view' | 'create' | 'edit' | 'delete' | 'cancel'>;
}

/** Build the standard 4 permissions for a document type — view, issue, cancel, email.
 *  Documents are immutable once issued, so :edit is intentionally absent.
 *  Anyone who can :view a document can already print/download it, so :export is intentionally absent. */
/**
 * Build the standard permissions for a document type.
 *
 * Deliberately no `:view`. Documents are read through the travel file's
 * Accounting tab, which is one view over all of them — a per-type read right
 * had nothing to attach to, and the eight that existed were never checked by
 * any code. The tab as a whole is gated on `travel_file.accounting:view`.
 * `voucher:view` survives as an explicit entry below because a dashboard
 * quick action genuinely checks it.
 */
function buildDocumentPerms<T extends string>(resource: T, displayName: string) {
   const displayNameLower = displayName.toLowerCase();
    return {
        [`${resource}:issue`]: makeMeta(`Issue ${displayName}`, `Generate a new ${displayNameLower}`, 'Travel File Documents'),
        [`${resource}:cancel`]: makeMeta(`Cancel ${displayName}`, `Cancel an issued ${displayNameLower}`, 'Travel File Documents'),
        [`${resource}:email`]: makeMeta(`Email ${displayName}`, `Send a ${displayNameLower} by email`, 'Travel File Documents')
    } as BuildPerms<T, 'issue' | 'cancel' | 'email'>;
}

/** Build the standard 2 permissions for a setup/lookup resource.
 *  Setup data is admin territory: granular CRUD distinctions don't reflect real org boundaries.
 *  If you can edit a lookup table, you can also add/remove rows. */
function buildSetupPerms<T extends string>(resource: T, displayName: string, category: string) {
    return {
        [`${resource}:view`]: makeMeta(`View ${displayName}`, '', category),
        [`${resource}:edit`]: makeMeta(
            `Manage ${displayName}`,
            `Add, edit, and delete ${displayName.toLowerCase()}`,
            category
        )
    } as BuildPerms<T, 'view' | 'edit'>;
}

// ─────────────────────────────────────────────────────────────────────────
// WILDCARD EXPANSION
// ─────────────────────────────────────────────────────────────────────────

/**
 * Keys no wildcard ever expands to.
 *
 * A right this sensitive should be granted because someone decided to grant it,
 * not because it happened to sit inside a group somebody swept up. The revenue
 * chart puts company turnover on the dashboard, and nine presets picked it up
 * without ever naming it — 'dashboard.*:view' in seven of them, plus '*:view'
 * on the auditor and '*:*' on the administrator.
 *
 * The key is still in the catalog and still has its checkbox: an administrator
 * can grant it deliberately, to anyone, and that grant survives everything here
 * because an exact key is returned before this set is consulted.
 */
// 'travel_file:adopt_from_tb' copies a TravelBooster file and its posted documents
// into Taytan — granted by name only, never swept up by 'travel_file:*' or '*:*'.
// 'travel_file:moved_accounting_override' posts to Hashavshevet next to TB on a
// moved file — finance only, by name.
// 'travel_file:confirm_without_payment' opens a file with no payment, giving it a
// Hashavshevet number — granted by name to the staff who confirm guides and free
// passengers.
export const WILDCARD_EXCLUDED_KEYS = new Set<string>([
    'dashboard.revenue_chart:view',
    'travel_file:adopt_from_tb',
    'travel_file:confirm_without_payment',
    'travel_file:moved_accounting_override'
]);

/**
 * Expand a wildcard like 'service.*:view' or '*:view' or '*:*' into the
 * concrete keys it matches in the catalog. Used at preset-application
 * time so roles are always stored with explicit permission lists.
 *
 * An exact key is returned untouched — only a wildcard is filtered, so naming
 * a WILDCARD_EXCLUDED_KEYS entry outright still grants it.
 */
export function expandWildcard(pattern: string): string[] {
    if (!pattern.includes('*')) return [pattern];

    const allKeys = Object.keys(PERMISSIONS);
    const [resourcePart, actionPart] = pattern.split(':');

    return allKeys.filter((key) => {
        if (WILDCARD_EXCLUDED_KEYS.has(key)) return false;
        const [keyResource, keyAction] = key.split(':');
        const resourceMatches =
            resourcePart === '*' ||
            keyResource === resourcePart ||
            (resourcePart.endsWith('.*') &&
                keyResource.startsWith(resourcePart.slice(0, -2) + '.'));
        const actionMatches = actionPart === '*' || keyAction === actionPart;
        return resourceMatches && actionMatches;
    });
}

/**
 * Expand a list of permission patterns into a flat list of concrete keys.
 * Used when applying a preset to a role.
 */
export function expandPermissionList(patterns: string[]): string[] {
    const expanded = new Set<string>();
    for (const pattern of patterns) {
        for (const key of expandWildcard(pattern)) {
            expanded.add(key);
        }
    }
    return [...expanded];
}

/**
 * Resolve a preset (with optional `extends`) into the final flat permission list.
 */
export function resolvePreset(presetKey: RolePresetKey): string[] {
    const preset = ROLE_PRESETS[presetKey] as Preset;
    const base = preset.extends ? resolvePreset(preset.extends as RolePresetKey) : [];
    const own = expandPermissionList([...preset.permissions, ...(preset.additional || [])]);
    return [...new Set([...base, ...own])];
}
